import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const InternalAgentScanner = () => {
  const [scans, setScans] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [tokenCompany, setTokenCompany] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'details' | 'agents' | 'setup'
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [registrationToken, setRegistrationToken] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);

  useEffect(() => {
    fetchScans();
    fetchAgents();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await axios.get(`${API}/scans/internal-agent`);
      setScans(response.data);
    } catch (error) {
      console.error("Error fetching Internal Agent scans:", error);
    }
  };

  const generateRegistrationToken = async () => {
    try {
      const response = await axios.post(`${API}/agent/generate-token`);
      setRegistrationToken(response.data.token);
      setTokenCompany(response.data.company_name || null);
      setShowTokenModal(true);
    } catch (error) {
      alert("Error generando token: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm('¿Está seguro de eliminar este agente?')) return;
    
    try {
      await axios.delete(`${API}/agents/${agentId}`);
      fetchAgents();
    } catch (error) {
      alert("Error eliminando agente: " + (error.response?.data?.detail || error.message));
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setViewMode('details');
    setActiveTab('summary');
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'text-red-700 bg-red-100 border-red-300',
      'high': 'text-orange-700 bg-orange-100 border-orange-300',
      'medium': 'text-yellow-700 bg-yellow-100 border-yellow-300',
      'low': 'text-blue-700 bg-blue-100 border-blue-300',
      'info': 'text-gray-700 bg-gray-100 border-gray-300'
    };
    return colors[severity] || colors['info'];
  };

  const getRiskScoreColor = (score) => {
    if (score >= 75) return 'text-red-600';
    if (score >= 50) return 'text-orange-500';
    if (score >= 25) return 'text-yellow-500';
    return 'text-green-500';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-AR');
  };

  // Vista de Setup/Instrucciones
  const renderSetupView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">🔧 Configuración del Agente Interno</h2>
        <p className="opacity-90">Instrucciones para instalar y ejecutar el agente en sistemas Windows</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Paso 1: Descargar el paquete del agente</h3>
        <p className="text-gray-600 mb-4">
          Descargue el paquete y descomprímalo en la máquina Windows que desea escanear.
          Incluye el agente, el compilador a <code>.exe</code> y el manual.
        </p>
        <div className="mb-4 flex gap-3">
          <a
            href="/downloads/CostillaTech-Agent-Package.zip"
            download
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            data-testid="download-agent-package"
          >
            ⬇️ Descargar paquete del agente (.zip)
          </a>
          <a
            href="/downloads/manual.html"
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50"
            data-testid="open-agent-manual"
          >
            📖 Ver manual
          </a>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 text-green-400 font-mono text-sm space-y-1">
          <p># El .zip contiene:</p>
          <p>costillatech_agent.py &nbsp;&nbsp;(el agente)</p>
          <p>BUILD_WINDOWS.bat &nbsp;(genera CostillaTech-Agent.exe en el equipo)</p>
          <p>manual.html / README.md</p>
        </div>
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <strong>Dos formas de usarlo.</strong> (A) Ejecutar <code>BUILD_WINDOWS.bat</code> una vez
          para generar <code>CostillaTech-Agent.exe</code> y después usar sólo el ejecutable, o
          (B) correr el agente directamente con Python. Ambas requieren
          <strong> Python 3 instalado</strong> en el equipo (la opción A sólo la primera vez, para compilar).
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Paso 2: Registrar el Agente</h3>
        <p className="text-gray-600 mb-4">
          Genere el Token de Registro con el botón de abajo, cópielo y regístrelo desde el equipo.
        </p>
        <div className="mb-4">
          <button
            onClick={generateRegistrationToken}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            data-testid="setup-generate-token-btn"
          >
            🔑 Generar Token de Registro
          </button>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 text-green-400 font-mono text-sm overflow-x-auto space-y-1">
          <p># Abrir CMD como administrador en la carpeta del agente</p>
          <p># Opción A - si compiló el ejecutable:</p>
          <p>CostillaTech-Agent.exe --register "PEGAR_TOKEN_AQUI"</p>
          <p></p>
          <p># Opción B - directo con Python:</p>
          <p>python costillatech_agent.py --register "PEGAR_TOKEN_AQUI"</p>
        </div>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">
            <strong>Importante:</strong> use el token generado con el botón
            "Generar Token de Registro" de arriba (también está en la pestaña Agentes).
            No sirve el token de sesión del navegador. El token es válido por 365 días.
          </p>
        </div>
        <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="agent-env-note">
          <p className="text-amber-800 text-sm">
            <strong>Mismo entorno:</strong> el paquete y el token deben provenir de este mismo
            entorno. El agente se conecta automáticamente a <code>https://dev.costillatech.com/api</code> leyéndolo
            del token. Si necesita forzarlo, agregue
            <code> --api-url https://dev.costillatech.com/api</code> al comando de registro.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Paso 3: Ejecutar Escaneo</h3>
        <p className="text-gray-600 mb-4">
          Una vez registrado, ejecute el escaneo completo o por módulos.
        </p>
        <div className="bg-gray-900 rounded-lg p-4 text-green-400 font-mono text-sm space-y-2">
          <p># Escaneo completo (.exe o con Python: python costillatech_agent.py --scan)</p>
          <p>CostillaTech-Agent.exe --scan</p>
          <p></p>
          <p># Solo SQL Server</p>
          <p>CostillaTech-Agent.exe --scan-sql</p>
          <p></p>
          <p># Solo Windows</p>
          <p>CostillaTech-Agent.exe --scan-windows</p>
          <p></p>
          <p># Solo Red Interna (puertos de este equipo)</p>
          <p>CostillaTech-Agent.exe --scan-network</p>
          <p></p>
          <p># Descubrimiento de red local (barrido 1-65535 de los hosts vivos)</p>
          <p>CostillaTech-Agent.exe --scan-discovery</p>
          <p></p>
          <p># Descubrimiento de una subred puntual (ej: laboratorio)</p>
          <p>CostillaTech-Agent.exe --scan-discovery --network 192.168.56.0/24 --yes</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-semibold mb-4">📊 Módulos de Escaneo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800">🗄️ SQL Server</h4>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• Versión y parches</li>
              <li>• Usuario SA habilitado</li>
              <li>• Procedimientos peligrosos</li>
              <li>• Cifrado de bases de datos</li>
            </ul>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800">🪟 Windows</h4>
            <ul className="text-sm text-green-700 mt-2 space-y-1">
              <li>• SMBv1 habilitado</li>
              <li>• Estado del Firewall</li>
              <li>• RDP expuesto</li>
              <li>• Antivirus instalado</li>
            </ul>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800">🌐 Red Interna + Descubrimiento</h4>
            <ul className="text-sm text-purple-700 mt-2 space-y-1">
              <li>• Todas las interfaces privadas del host</li>
              <li>• Hosts vivos por ARP / ICMP / TCP</li>
              <li>• Barrido completo 1-65535 por host</li>
              <li>• Catálogo de 90+ servicios y protocolos inseguros</li>
            </ul>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <h4 className="font-semibold text-orange-800">📦 Aplicaciones</h4>
            <ul className="text-sm text-orange-700 mt-2 space-y-1">
              <li>• Frameworks instalados</li>
              <li>• Versiones EOL</li>
              <li>• Configs inseguras</li>
              <li>• Inventario de software</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Vista de Agentes Registrados
  const renderAgentsView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Agentes Registrados</h2>
          <p className="opacity-90">{agents.length} agente(s) conectado(s)</p>
        </div>
        <button
          onClick={generateRegistrationToken}
          className="px-4 py-2 bg-white text-green-700 rounded-lg hover:bg-green-50 font-medium text-sm"
          data-testid="generate-token-btn"
        >
          Generar Token de Registro
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🔌</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay agentes registrados</h3>
          <p className="text-gray-500 mb-4">
            Instale el agente en sus sistemas Windows para comenzar a escanear.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={generateRegistrationToken}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              🔑 Generar Token de Registro
            </button>
            <button
              onClick={() => setViewMode('setup')}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Ver Instrucciones de Instalación
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {agents.map((agent) => (
            <div key={agent.agent_id} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    💻 {agent.hostname}
                  </h3>
                  <p className="text-sm text-gray-500">{agent.os_info}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  agent.status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {agent.status === 'active' ? '● Activo' : '○ Inactivo'}
                </span>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Agent ID:</span>
                  <span className="ml-2 font-mono text-gray-700">{agent.agent_id.slice(0, 8)}...</span>
                </div>
                <div>
                  <span className="text-gray-500">Versión:</span>
                  <span className="ml-2 text-gray-700">{agent.agent_version}</span>
                </div>
                <div>
                  <span className="text-gray-500">Registrado:</span>
                  <span className="ml-2 text-gray-700">{formatDate(agent.created_at)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Última conexión:</span>
                  <span className="ml-2 text-gray-700">{formatDate(agent.last_seen)}</span>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => deleteAgent(agent.agent_id)}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Vista de Lista de Scans
  const renderListView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">🖥️ Internal Agent Scanner</h2>
        <p className="opacity-90">Escaneo de sistemas legacy, redes internas y bases de datos SQL Server</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-indigo-500">
          <div className="text-sm text-gray-500">Total Escaneos</div>
          <div className="text-2xl font-bold text-indigo-600">{scans.length}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Vulns Críticas</div>
          <div className="text-2xl font-bold text-red-600">
            {scans.reduce((acc, s) => acc + (s.critical_count || 0), 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-orange-500">
          <div className="text-sm text-gray-500">Vulns Altas</div>
          <div className="text-2xl font-bold text-orange-600">
            {scans.reduce((acc, s) => acc + (s.high_count || 0), 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-500">Agentes Activos</div>
          <div className="text-2xl font-bold text-green-600">{agents.length}</div>
        </div>
      </div>

      {/* Scans List */}
      {scans.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">📡</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay escaneos aún</h3>
          <p className="text-gray-500 mb-4">
            Instale el agente en sus sistemas y ejecute el primer escaneo.
          </p>
          <button
            onClick={() => setViewMode('setup')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Ver Instrucciones
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">📋 Historial de Escaneos</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {scans.map((scan) => (
              <div 
                key={scan.id} 
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => viewScanDetails(scan)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-800">💻 {scan.hostname}</h4>
                    <p className="text-sm text-gray-500">{formatDate(scan.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${getRiskScoreColor(scan.risk_score)}`}>
                      {scan.risk_score}
                    </div>
                    <div className="text-xs text-gray-500">Risk Score</div>
                  </div>
                </div>
                
                <div className="mt-3 flex flex-wrap gap-2">
                  {scan.critical_count > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                      {scan.critical_count} Critical
                    </span>
                  )}
                  {scan.high_count > 0 && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                      {scan.high_count} High
                    </span>
                  )}
                  {scan.medium_count > 0 && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                      {scan.medium_count} Medium
                    </span>
                  )}
                  {scan.low_count > 0 && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {scan.low_count} Low
                    </span>
                  )}
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    Total: {scan.total_vulnerabilities}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Vista de Detalle del Scan
  const renderDetailsView = () => {
    if (!selectedScan) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <button
            onClick={() => setViewMode('list')}
            className="text-white/80 hover:text-white mb-2 flex items-center"
          >
            ← Volver al listado
          </button>
          <h2 className="text-2xl font-bold">💻 {selectedScan.hostname}</h2>
          <p className="opacity-90">{formatDate(selectedScan.created_at)}</p>
        </div>

        {/* Risk Score Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Risk Score</h3>
              <p className="text-sm text-gray-500">Basado en vulnerabilidades detectadas</p>
            </div>
            <div className={`text-5xl font-bold ${getRiskScoreColor(selectedScan.risk_score)}`}>
              {selectedScan.risk_score}
              <span className="text-lg text-gray-400">/100</span>
            </div>
          </div>
          
          {/* Vulnerability Summary */}
          <div className="mt-4 grid grid-cols-5 gap-2">
            <div className="text-center p-2 bg-red-50 rounded">
              <div className="text-xl font-bold text-red-600">{selectedScan.critical_count}</div>
              <div className="text-xs text-red-600">Critical</div>
            </div>
            <div className="text-center p-2 bg-orange-50 rounded">
              <div className="text-xl font-bold text-orange-600">{selectedScan.high_count}</div>
              <div className="text-xs text-orange-600">High</div>
            </div>
            <div className="text-center p-2 bg-yellow-50 rounded">
              <div className="text-xl font-bold text-yellow-600">{selectedScan.medium_count}</div>
              <div className="text-xs text-yellow-600">Medium</div>
            </div>
            <div className="text-center p-2 bg-blue-50 rounded">
              <div className="text-xl font-bold text-blue-600">{selectedScan.low_count}</div>
              <div className="text-xs text-blue-600">Low</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded">
              <div className="text-xl font-bold text-gray-600">{selectedScan.info_count}</div>
              <div className="text-xs text-gray-600">Info</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['summary', 'windows', 'network', 'discovery', 'sql', 'compliance', 'recommendations', 'software'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeTab === tab
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'summary' && '📊 Resumen'}
                  {tab === 'windows' && '🪟 Windows'}
                  {tab === 'network' && '🌐 Red'}
                  {tab === 'discovery' && '🛰️ Descubrimiento'}
                  {tab === 'sql' && '🗄️ SQL Server'}
                  {tab === 'compliance' && '✅ Compliance'}
                  {tab === 'recommendations' && '💡 Recomendaciones'}
                  {tab === 'software' && '📦 Software'}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'summary' && renderSummaryTab()}
            {activeTab === 'windows' && renderWindowsTab()}
            {activeTab === 'network' && renderNetworkTab()}
            {activeTab === 'discovery' && renderDiscoveryTab()}
            {activeTab === 'sql' && renderSQLTab()}
            {activeTab === 'compliance' && renderComplianceTab()}
            {activeTab === 'recommendations' && renderRecommendationsTab()}
            {activeTab === 'software' && renderSoftwareTab()}
          </div>
        </div>
      </div>
    );
  };

  const renderSummaryTab = () => {
    const allVulns = [
      ...(selectedScan.configurations?.vulnerabilities || []),
      ...(selectedScan.patching?.vulnerabilities || []),
      ...(selectedScan.dependencies?.vulnerabilities || []),
      ...(selectedScan.network_exposure?.vulnerabilities || []),
      ...(selectedScan.network_discovery?.vulnerabilities || []),
      ...(selectedScan.windows?.vulnerabilities || []),
      ...(selectedScan.network?.vulnerabilities || []),
      ...(selectedScan.sql_server?.vulnerabilities || []),
      ...(selectedScan.applications?.vulnerabilities || [])
    ];

    return (
      <div className="space-y-6">
        <h4 className="text-lg font-semibold">Todas las Vulnerabilidades ({allVulns.length})</h4>
        
        {allVulns.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">✅</div>
            <p>No se encontraron vulnerabilidades</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allVulns.sort((a, b) => {
              const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
              return order[a.severity] - order[b.severity];
            }).map((vuln, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(vuln.severity)}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${getSeverityColor(vuln.severity)}`}>
                      {vuln.severity}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{vuln.category}</span>
                  </div>
                  {vuln.cvss_score && (
                    <span className="text-sm font-semibold">CVSS: {vuln.cvss_score}</span>
                  )}
                </div>
                <h5 className="font-semibold mt-2">{vuln.title}</h5>
                <p className="text-sm text-gray-600 mt-1">{vuln.description}</p>
                <div className="mt-2 text-sm">
                  <span className="text-gray-500">Componente:</span> {vuln.affected_component}
                </div>
                {vuln.cve_id && (
                  <div className="mt-1 text-sm">
                    <span className="text-gray-500">CVE:</span> 
                    <a 
                      href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:underline ml-1"
                    >
                      {vuln.cve_id}
                    </a>
                  </div>
                )}
                <div className="mt-3 p-3 bg-green-50 rounded text-sm">
                  <span className="font-semibold text-green-700">💡 Remediación:</span>
                  <p className="text-green-800 mt-1">{vuln.remediation}</p>
                {/* Security Frameworks */}
                {vuln.frameworks && Object.keys(vuln.frameworks).some(k => vuln.frameworks[k]?.length > 0) && (
                  <div className="mt-3 p-3 bg-blue-50 rounded text-sm">
                    <span className="font-semibold text-blue-700">🔐 Frameworks de Seguridad:</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {vuln.frameworks.iso27001?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">ISO 27001</span>
                          <span className="text-xs text-gray-600">{vuln.frameworks.iso27001.join(", ")}</span>
                        </div>
                      )}
                      {vuln.frameworks.owasp?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">OWASP</span>
                          <span className="text-xs text-gray-600">{vuln.frameworks.owasp.join(", ")}</span>
                        </div>
                      )}
                      {vuln.frameworks.nist?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">NIST</span>
                          <span className="text-xs text-gray-600">{vuln.frameworks.nist.join(", ")}</span>
                        </div>
                      )}
                      {vuln.frameworks.cis?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">CIS</span>
                          <span className="text-xs text-gray-600">{vuln.frameworks.cis.join(", ")}</span>
                        </div>
                      )}
                      {vuln.frameworks.pci_dss?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">PCI-DSS</span>
                          <span className="text-xs text-gray-600">{vuln.frameworks.pci_dss.join(", ")}</span>
                        </div>
                      )}
                      {vuln.frameworks.mitre_attack?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">MITRE ATT&CK</span>
                          <span className="text-xs text-gray-600">{vuln.frameworks.mitre_attack.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderWindowsTab = () => {
    const win = selectedScan.windows;
    if (!win) return <p className="text-gray-500">No hay datos de Windows disponibles</p>;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Sistema Operativo</div>
            <div className="font-semibold text-gray-800">{win.os_version || 'N/A'}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Build</div>
            <div className="font-semibold text-gray-800">{win.os_build || 'N/A'}</div>
          </div>
          <div className={`p-4 rounded-lg ${win.firewall_enabled ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="text-sm text-gray-500">Firewall</div>
            <div className={`font-semibold ${win.firewall_enabled ? 'text-green-700' : 'text-red-700'}`}>
              {win.firewall_enabled ? '✅ Habilitado' : '❌ Deshabilitado'}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${win.antivirus_installed ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="text-sm text-gray-500">Antivirus</div>
            <div className={`font-semibold ${win.antivirus_installed ? 'text-green-700' : 'text-red-700'}`}>
              {win.antivirus_installed ? `✅ ${win.antivirus_name}` : '❌ No detectado'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg border ${win.smb_v1_enabled ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className="text-sm text-gray-500">SMBv1</div>
            <div className={`font-semibold ${win.smb_v1_enabled ? 'text-red-700' : 'text-green-700'}`}>
              {win.smb_v1_enabled ? '⚠️ Habilitado (Riesgo)' : '✅ Deshabilitado'}
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${win.rdp_exposed ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="text-sm text-gray-500">RDP</div>
            <div className={`font-semibold ${win.rdp_exposed ? 'text-yellow-700' : 'text-gray-700'}`}>
              {win.rdp_exposed ? '⚠️ Habilitado' : '○ Deshabilitado'}
            </div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border">
            <div className="text-sm text-gray-500">Updates Pendientes</div>
            <div className="font-semibold text-gray-800">{win.pending_updates || 0}</div>
          </div>
        </div>

        {win.vulnerabilities && win.vulnerabilities.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3">Vulnerabilidades de Windows</h4>
            <div className="space-y-2">
              {win.vulnerabilities.map((v, i) => (
                <div key={i} className={`p-3 rounded border ${getSeverityColor(v.severity)}`}>
                  <span className={`text-xs font-bold uppercase ${getSeverityColor(v.severity)} px-2 py-1 rounded`}>
                    {v.severity}
                  </span>
                  <span className="ml-2 font-medium">{v.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderNetworkTab = () => {
    const net = selectedScan.network;
    if (!net) return <p className="text-gray-500">No hay datos de red disponibles</p>;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">IP Local</div>
            <div className="font-mono font-semibold text-gray-800">{net.local_ip || 'N/A'}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Gateway</div>
            <div className="font-mono font-semibold text-gray-800">{net.gateway || 'N/A'}</div>
          </div>
        </div>

        {net.open_ports && net.open_ports.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3">Puertos Abiertos ({net.open_ports.length})</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {net.open_ports.map((port, i) => (
                <div 
                  key={i} 
                  className={`p-3 rounded border ${
                    port.risk === 'critical' ? 'bg-red-50 border-red-200' :
                    port.risk === 'high' ? 'bg-orange-50 border-orange-200' :
                    port.risk === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="font-mono font-bold">{port.port}</div>
                  <div className="text-sm text-gray-600">{port.service}</div>
                  <div className={`text-xs font-medium ${
                    port.risk === 'critical' ? 'text-red-600' :
                    port.risk === 'high' ? 'text-orange-600' :
                    port.risk === 'medium' ? 'text-yellow-600' :
                    'text-gray-500'
                  }`}>
                    {port.risk?.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {net.vulnerabilities && net.vulnerabilities.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3">Vulnerabilidades de Red</h4>
            <div className="space-y-2">
              {net.vulnerabilities.map((v, i) => (
                <div key={i} className={`p-3 rounded border ${getSeverityColor(v.severity)}`}>
                  <span className={`text-xs font-bold uppercase ${getSeverityColor(v.severity)} px-2 py-1 rounded`}>
                    {v.severity}
                  </span>
                  <span className="ml-2 font-medium">{v.title}</span>
                  <p className="text-sm text-gray-600 mt-1">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDiscoveryTab = () => {
    const d = selectedScan.network_discovery;
    if (!d) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="discovery-empty-state">
          <p className="text-gray-700 font-medium">Este escaneo no incluye descubrimiento de red.</p>
          <p className="text-sm text-gray-500 mt-1">
            Ejecutá <code className="bg-gray-200 px-1 rounded">CostillaTech-Agent.exe --scan-discovery</code> para
            mapear la red local del equipo (barrido completo 1-65535 en los hosts vivos).
          </p>
        </div>
      );
    }
    const hosts = d.hosts_discovered || [];
    const riskBox = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-gray-100 text-gray-600 border-gray-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200'
    };

    return (
      <div className="space-y-6" data-testid="discovery-tab-content">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Hosts vivos</div>
            <div className="text-2xl font-bold text-gray-800" data-testid="discovery-hosts-count">{d.total_hosts_alive || 0}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Puertos abiertos</div>
            <div className="text-2xl font-bold text-gray-800" data-testid="discovery-ports-count">{d.total_open_ports || 0}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Rango escaneado</div>
            <div className="font-mono font-semibold text-gray-800" data-testid="discovery-port-range">{d.port_range || 'N/A'}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Duración</div>
            <div className="font-semibold text-gray-800" data-testid="discovery-duration">{d.duration_seconds || 0}s</div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-2">Redes escaneadas</h4>
          <div className="flex flex-wrap gap-2" data-testid="discovery-networks">
            {(d.networks_scanned || []).map((n, i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-sm font-mono">
                {n.cidr}{n.interface_ip ? ` · iface ${n.interface_ip}` : ''}{n.truncated ? ' · reducida a /24' : ''}
              </span>
            ))}
            {(d.networks_skipped || []).map((n, i) => (
              <span key={`sk-${i}`} className="px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-500 text-sm font-mono line-through">
                {n}
              </span>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Hosts descubiertos ({hosts.length})</h4>
          <div className="space-y-3" data-testid="discovery-hosts-list">
            {hosts.map((h, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4" data-testid={`discovery-host-${h.ip}`}>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="font-mono font-bold text-gray-900">{h.ip}</span>
                  {h.is_self && <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">este equipo</span>}
                  {h.hostname && <span className="text-sm text-gray-600">{h.hostname}</span>}
                  {h.mac && <span className="text-xs font-mono text-gray-400">{h.mac}</span>}
                  <span className="text-xs text-gray-500">descubierto por: {(h.alive_via || []).join(' / ')}</span>
                  <span className="ml-auto text-sm text-gray-500">{(h.open_ports || []).length} puerto(s)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(h.open_ports || []).map((p, j) => (
                    <span key={j} title={p.banner || ''}
                      className={`px-2 py-1 rounded border text-xs font-mono ${riskBox[p.risk] || riskBox.low}`}>
                      {p.port} · {p.service}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {d.vulnerabilities && d.vulnerabilities.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3">Hallazgos de red ({d.vulnerabilities.length})</h4>
            <div className="space-y-2" data-testid="discovery-findings">
              {d.vulnerabilities.map((v, i) => (
                <div key={i} className={`p-3 rounded border ${getSeverityColor(v.severity)}`}>
                  <span className="text-xs font-bold uppercase">{v.severity}</span>
                  <span className="ml-2 font-medium">{v.title}</span>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSQLTab = () => {
    const sql = selectedScan.sql_server;
    if (!sql) return <p className="text-gray-500">No hay datos de SQL Server disponibles</p>;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Versión</div>
            <div className="font-semibold text-gray-800">{sql.version || 'N/A'}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Edición</div>
            <div className="font-semibold text-gray-800">{sql.edition || 'N/A'}</div>
          </div>
          <div className={`p-4 rounded-lg ${sql.is_sa_enabled ? 'bg-red-50' : 'bg-green-50'}`}>
            <div className="text-sm text-gray-500">Usuario SA</div>
            <div className={`font-semibold ${sql.is_sa_enabled ? 'text-red-700' : 'text-green-700'}`}>
              {sql.is_sa_enabled ? '⚠️ Habilitado' : '✅ Deshabilitado'}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${sql.ssl_enabled ? 'bg-green-50' : 'bg-yellow-50'}`}>
            <div className="text-sm text-gray-500">SSL/TLS</div>
            <div className={`font-semibold ${sql.ssl_enabled ? 'text-green-700' : 'text-yellow-700'}`}>
              {sql.ssl_enabled ? '✅ Habilitado' : '⚠️ No habilitado'}
            </div>
          </div>
        </div>

        {sql.dangerous_procedures_enabled && sql.dangerous_procedures_enabled.length > 0 && (
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-2">⚠️ Procedimientos Peligrosos Habilitados</h4>
            <div className="flex flex-wrap gap-2">
              {sql.dangerous_procedures_enabled.map((proc, i) => (
                <span key={i} className="px-3 py-1 bg-red-100 text-red-700 rounded font-mono text-sm">
                  {proc}
                </span>
              ))}
            </div>
          </div>
        )}

        {sql.vulnerabilities && sql.vulnerabilities.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3">Vulnerabilidades de SQL Server</h4>
            <div className="space-y-2">
              {sql.vulnerabilities.map((v, i) => (
                <div key={i} className={`p-3 rounded border ${getSeverityColor(v.severity)}`}>
                  <span className={`text-xs font-bold uppercase ${getSeverityColor(v.severity)} px-2 py-1 rounded`}>
                    {v.severity}
                  </span>
                  <span className="ml-2 font-medium">{v.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };


  // Función para obtener color de prioridad
  const getPriorityColor = (priority) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800 border-red-200',
      'HIGH': 'bg-orange-100 text-orange-800 border-orange-200',
      'MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[priority] || colors['MEDIUM'];
  };

  // Tab de Compliance
  const renderComplianceTab = () => {
    const compliance = selectedScan.compliance || {};
    const patching = selectedScan.patching || {};
    const dependencies = selectedScan.dependencies || {};
    const configurations = selectedScan.configurations || {};
    
    return (
      <div className="space-y-6">
        {/* Estado General */}
        <div className={`p-4 rounded-lg border-2 ${
          compliance.overall_status?.includes('CRITICO') 
            ? 'bg-red-50 border-red-300' 
            : compliance.overall_status?.includes('NO COMPLIANT')
              ? 'bg-orange-50 border-orange-300'
              : 'bg-green-50 border-green-300'
        }`}>
          <h4 className="font-bold text-lg mb-2">Estado de Compliance</h4>
          <p className={`text-xl font-semibold ${
            compliance.overall_status?.includes('CRITICO') ? 'text-red-700' :
            compliance.overall_status?.includes('NO COMPLIANT') ? 'text-orange-700' : 'text-green-700'
          }`}>
            {compliance.overall_status || 'No evaluado'}
          </p>
          {compliance.risk_score !== undefined && (
            <p className="text-sm text-gray-600 mt-1">Risk Score: <span className="font-bold">{compliance.risk_score}</span></p>
          )}
        </div>

        {/* Grid de normativas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* PCI-DSS */}
          <div className={`p-4 rounded-lg border ${compliance.pci_dss?.compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h5 className="font-semibold flex items-center gap-2">
              {compliance.pci_dss?.compliant ? '✅' : '❌'} PCI-DSS
            </h5>
            {compliance.pci_dss?.issues?.length > 0 && (
              <ul className="mt-2 text-sm space-y-1">
                {compliance.pci_dss.issues.map((issue, i) => (
                  <li key={i} className="text-red-700">• {issue}</li>
                ))}
              </ul>
            )}
          </div>

          {/* GDPR */}
          <div className={`p-4 rounded-lg border ${compliance.gdpr?.compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h5 className="font-semibold flex items-center gap-2">
              {compliance.gdpr?.compliant ? '✅' : '❌'} GDPR
            </h5>
            {compliance.gdpr?.issues?.length > 0 && (
              <ul className="mt-2 text-sm space-y-1">
                {compliance.gdpr.issues.map((issue, i) => (
                  <li key={i} className="text-red-700">• {issue}</li>
                ))}
              </ul>
            )}
          </div>

          {/* ISO 27001 */}
          <div className={`p-4 rounded-lg border ${compliance.iso27001?.compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h5 className="font-semibold flex items-center gap-2">
              {compliance.iso27001?.compliant ? '✅' : '❌'} ISO 27001
            </h5>
            {compliance.iso27001?.issues?.length > 0 && (
              <ul className="mt-2 text-sm space-y-1">
                {compliance.iso27001.issues.map((issue, i) => (
                  <li key={i} className="text-red-700">• {issue}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Sección de Parches */}
        {patching && (
          <div className="bg-white rounded-lg border p-4">
            <h5 className="font-semibold mb-3">🔄 Estado de Parches</h5>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Tipo de Sistema:</span>
                <p className="font-bold">{patching.os_type === 'server' ? '🖥️ Windows Server' : '💻 Windows Desktop'}</p>
              </div>
              <div>
                <span className="text-gray-500">Parches instalados:</span>
                <p className="font-bold">{patching.installed_patches_count || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Último parche:</span>
                <p className="font-bold">{patching.last_patch_date || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Días sin actualizar:</span>
                <p className={`font-bold ${(patching.days_since_last_patch || 0) > 90 ? 'text-red-600' : 'text-green-600'}`}>
                  {patching.days_since_last_patch ?? 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Estado:</span>
                <p className={`font-bold ${
                  patching.status === 'critical' ? 'text-red-600' :
                  patching.status === 'warning' ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {patching.status === 'ok' ? '✅ ACTUALIZADO' : patching.status?.toUpperCase() || 'N/A'}
                </p>
              </div>
            </div>
            {patching.os_type === 'desktop' && patching.status === 'ok' && (
              <p className="text-xs text-gray-500 mt-2">
                ℹ️ Windows Desktop usa parches acumulativos. La cantidad de parches en wmic qfe puede ser menor que en Windows Server.
              </p>
            )}
          </div>
        )}

        {/* Sección de Dependencias Legacy */}
        {dependencies && dependencies.legacy_dlls_found?.length > 0 && (
          <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
            <h5 className="font-semibold mb-3">⚠️ Dependencias Legacy Detectadas</h5>
            {dependencies.detection_method && (
              <p className="text-xs text-gray-500 mb-2">
                Método de detección: {dependencies.detection_method === 'win32api' ? 'Avanzado (win32api)' : 'Básico (PowerShell)'}
                {dependencies.os_type && ` | Sistema: ${dependencies.os_type === 'server' ? 'Windows Server' : 'Windows Desktop'}`}
              </p>
            )}
            <div className="space-y-2">
              {dependencies.legacy_dlls_found.map((dll, i) => (
                <div key={i} className={`p-3 rounded border ${
                  dll.is_maintained ? 'bg-green-50 border-green-200' : 
                  dll.severity === 'critical' ? 'bg-red-50 border-red-300' :
                  dll.severity === 'high' ? 'bg-orange-50 border-orange-300' :
                  dll.severity === 'info' ? 'bg-gray-50 border-gray-200' :
                  'bg-white border-gray-200'
                }`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <span className="font-mono text-sm font-semibold">{dll.name}</span>
                      <span className="text-gray-500 text-sm ml-2">({dll.description})</span>
                      {dll.version && (
                        <div className="text-xs text-gray-600 mt-1">
                          Versión: <span className="font-mono">{dll.version}</span>
                          {dll.product_name && <span className="ml-2">| {dll.product_name}</span>}
                        </div>
                      )}
                      {dll.status && (
                        <div className={`text-xs mt-1 ${
                          dll.is_maintained ? 'text-green-600' : 
                          dll.is_legacy ? 'text-orange-600' : 'text-gray-500'
                        }`}>
                          {dll.is_maintained ? '✅ ' : dll.is_legacy ? '⚠️ ' : 'ℹ️ '}{dll.status}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        dll.severity === 'critical' ? 'bg-red-200 text-red-800' :
                        dll.severity === 'high' ? 'bg-orange-200 text-orange-800' :
                        dll.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                        dll.severity === 'low' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {dll.severity ? dll.severity.toUpperCase() : `Año ${dll.year}`}
                      </span>
                      {dll.year && dll.severity && (
                        <span className="text-xs text-gray-400">Año {dll.year}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {dependencies.total_issues !== undefined && (
              <p className="text-sm text-gray-600 mt-3">
                Total: {dependencies.legacy_dlls_found.length} DLLs detectadas | 
                <span className={dependencies.total_issues > 0 ? ' text-orange-600 font-semibold' : ' text-green-600'}>
                  {dependencies.total_issues > 0 ? ` ${dependencies.total_issues} con riesgo real` : ' Sin riesgos críticos'}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Sección de Configuraciones */}
        {configurations && configurations.credentials_exposed > 0 && (
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <h5 className="font-semibold mb-3 text-red-800">🔐 Credenciales Expuestas</h5>
            <p className="text-red-700">
              Se encontraron <span className="font-bold">{configurations.credentials_exposed}</span> archivos 
              con credenciales en texto plano de un total de {configurations.config_files_found} archivos de configuración escaneados.
            </p>
            {configurations.files_with_issues?.length > 0 && (
              <ul className="mt-2 text-sm space-y-1">
                {configurations.files_with_issues.slice(0, 5).map((file, i) => (
                  <li key={i} className="text-red-600">• {file.file} ({file.issue})</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  // Tab de Recomendaciones
  const renderRecommendationsTab = () => {
    const recommendations = selectedScan.recommendations || [];
    
    if (recommendations.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">
          <p>No hay recomendaciones disponibles para este escaneo.</p>
          <p className="text-sm mt-2">Ejecute un escaneo completo para generar recomendaciones.</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        <h4 className="font-semibold text-lg">Plan de Remediación Priorizado</h4>
        
        {recommendations.map((rec, index) => (
          <div 
            key={index} 
            className={`p-4 rounded-lg border-2 ${getPriorityColor(rec.priority)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    rec.priority === 'CRITICAL' ? 'bg-red-600 text-white' :
                    rec.priority === 'HIGH' ? 'bg-orange-500 text-white' :
                    rec.priority === 'MEDIUM' ? 'bg-yellow-500 text-white' : 'bg-blue-500 text-white'
                  }`}>
                    {rec.priority}
                  </span>
                  <span className="text-sm text-gray-600">{rec.timeline}</span>
                </div>
                <h5 className="font-semibold text-lg">{rec.title}</h5>
                <p className="text-gray-700 mt-1">{rec.description}</p>
                
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  {rec.cost_estimate && (
                    <div>
                      <span className="text-gray-500">💰 Costo estimado:</span>
                      <p className="font-medium">{rec.cost_estimate}</p>
                    </div>
                  )}
                  {rec.impact && (
                    <div>
                      <span className="text-gray-500">📈 Impacto:</span>
                      <p className="font-medium">{rec.impact}</p>
                    </div>
                  )}
                  {rec.effort && (
                    <div>
                      <span className="text-gray-500">⚡ Esfuerzo:</span>
                      <p className="font-medium">{rec.effort}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSoftwareTab = () => {
    const inventory = selectedScan.software_inventory || [];
    
    return (
      <div className="space-y-6">
        <h4 className="font-semibold">Inventario de Software ({inventory.length})</h4>
        
        {inventory.length === 0 ? (
          <p className="text-gray-500">No hay datos de inventario disponibles</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versión</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publisher</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inventory.map((sw, i) => (
                  <tr key={i} className={sw.is_eol ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{sw.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{sw.version}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{sw.publisher || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {sw.is_eol ? (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                          ⚠️ EOL
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          ✅ OK
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Modal para mostrar el token de registro
  const TokenModal = () => {
    if (!showTokenModal) return null;
    
    const copyToken = () => {
      navigator.clipboard.writeText(registrationToken);
      alert('Token copiado al portapapeles');
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4">
          <h3 className="text-xl font-bold mb-4">🔑 Token de Registro Generado</h3>
          
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4" data-testid="token-company-warning">
            <p className="text-sm text-amber-900">
              Este token registra agentes en la empresa{' '}
              <strong data-testid="token-company-name">{tokenCompany || 'actual'}</strong>. Todos los escaneos de ese
              agente van a quedar asociados a esa empresa, no a la del usuario que después mire el panel.
            </p>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-600 mb-2">Use este token para registrar el agente:</p>
            <div className="bg-gray-800 text-green-400 p-3 rounded font-mono text-sm break-all">
              {registrationToken}
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-blue-800 mb-2">Comando para registrar:</p>
            <code className="bg-blue-100 px-2 py-1 rounded text-blue-900 text-sm">
              CostillaTech-Agent.exe --register "{registrationToken?.substring(0, 20)}..."
            </code>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={copyToken}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              📋 Copiar Token
            </button>
            <button
              onClick={() => setShowTokenModal(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <TokenModal />
      {/* Navigation */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => { setViewMode('list'); fetchScans(); }}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'list' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          📋 Escaneos
        </button>
        <button
          onClick={() => { setViewMode('agents'); fetchAgents(); }}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'agents' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          🤖 Agentes ({agents.length})
        </button>
        <button
          onClick={() => setViewMode('setup')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'setup' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          🔧 Configuración
        </button>
      </div>

      {/* Content */}
      {viewMode === 'list' && renderListView()}
      {viewMode === 'details' && renderDetailsView()}
      {viewMode === 'agents' && renderAgentsView()}
      {viewMode === 'setup' && renderSetupView()}
    </div>
  );
};

export default InternalAgentScanner;
